import styles from "./SearchBoxAutoSuggest.module.scss";
import { connect, CustomContainer } from "../../atoms/DependencyHelper";
import config from "../../../config/index";
import React, { forwardRef, useImperativeHandle, useEffect,useState, useCallback } from "react";
import { getProductSearchList } from "../../../services/productListService";
import {
    link as AnalyticsLink,
    getUtagData as AnalyticsGetUtagData
}from "../../../services/analyticsUtag";
import labels from "../../../config/labels";
import { componentResize, componentCheckSize, debounce } from "../../../utils/helperServices";

const SearchBoxAutoSuggest = forwardRef(
(
    {
        closeRequired = true,
        className,
        inputStyle,
        handleKeydown,
        handleSearchSelect,
        handleSearchClick,
        handleSearchTextChange,
        focus,
        handleFilterError,
        showAffordabilityLabel,
        qualifyLabel,
    }, 
    ref
) => {
	const env = config.env;
	const imagePrefix = config.envConfigs[env].imagePrefix;
	const inputAutoFocus = React.createRef();
	const [searchText, setSearchText] = useState('');
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [showOptions, setShowOptions] = useState(false);
    const [optionSelected, setOptionSelected] = useState(false);
    const [inputFocus, setInputFocus] = useState(false);
    const [searchIcon, setSearchIcon] = useState("default");
    const optionSize = 8;
    const infoIconPath = `${imagePrefix}/assets/images/icon-info-grey.svg`;
    const isMobile = componentResize();
    const isTablet = componentCheckSize() <= 768;

    const analyticsTags = [
        "page_channel",
        "page_master_tab",
        "visitor_login_status",
        "visitor_customer_id",
        "visitor_id_hash",
        "visitor_msisdn_hash",
        "visitor_subscription_type",
    ];
    
    const pageName = AnalyticsGetUtagData(["page_name"]).page_name;

    useImperativeHandle(ref, () => ({
        clearText: () => {
            setShowOptions(false);
            setSearchText("");
            handleSearchTextChange("");
            inputAutoFocus.current.focus();
        }
      }));

    useEffect(() => {
        const abortCtrl = new AbortController();
        const opts = abortCtrl.signal;
        if (searchText.length > 2 && !optionSelected) {
            debounceSearch(opts, searchText);
        } else {
            setShowOptions(false);
        }
        return () => {
          abortCtrl && abortCtrl.abort();
        };
    }, [searchText]);

    const debounceSearch = useCallback(
        debounce((signal, text) => {
          fetchData(signal, text);
        }, 200),
        []
    );

    const fetchData = async (signal, text) => {
        try {
          let currentItemCategory = -1;
          let productSearchList = await getProductSearchList(
            text,
            currentItemCategory,
            signal
          );
          let newList = productSearchList.map((option) => {
              return {
                  ...option,
                  value: option.sku,
                  label: option.name
              }
          })
          setFilteredOptions(newList);
          setShowOptions(true);
          handleFilterError(false);
        } catch (err) {
          setShowOptions(false);
          handleFilterError(true);

          AnalyticsLink({
            page_name: `${pageName}`,
            page_form_event: "error",
            page_errors: labels.errors.lbl_deals_filter_error,
            page_error_type: "page",
            ...AnalyticsGetUtagData(analyticsTags),
          });
        }
      };

    const handleKeypress = (e) => {
        if (e.keyCode === 13){
            const abortCtrl = new AbortController();
            const opts = abortCtrl.signal;
            debounceSearch(opts, searchText);
        }
    }

    const handleOnChange = (e) => {
        setSearchText(e.target.value);
        setOptionSelected(false);
        handleSearchTextChange(e.target.value);
    }

    const handleOptionClick = (e, option) => {
        try {
            handleSearchSelect({ value: option.value, label: option.label, brandSku: option?.item?.manufacturer || "", brandName: option?.item?.manufacturer_label || "" }); //pass selected value to caller
            handleSearchTextChange(option.label);
            setShowOptions(false);
            setSearchText(option.label);
            setOptionSelected(true);
            inputAutoFocus.current.focus();

            AnalyticsLink({
                page_name: `${pageName}`,
                search_event: "search",
                search_terms: option.label,
                search_category: "product search",
                search_type: "suggested searches",
                search_results: filteredOptions.length,
                ...AnalyticsGetUtagData(analyticsTags),
            });
            setFilteredOptions([]);
        } catch (err) {
            setShowOptions(false);
            handleFilterError(true);
  
            AnalyticsLink({
              page_name: `${pageName}`,
              page_form_event: "error",
              page_errors: labels.errors.lbl_deals_filter_error,
              page_error_type: "page",
              ...AnalyticsGetUtagData(analyticsTags),
            });
        }
    }

    const handleCancel = () => {
        setShowOptions(false);
        setSearchText("");
        handleSearchTextChange("");
        inputAutoFocus.current.focus();
    }

    const truncateString = (str, stringLen) => {
        return str.length > stringLen ? str.substring(0, stringLen) + "..." : str;
    }

    const handleClick = () => {
        if(!inputFocus) {
            const pageName = AnalyticsGetUtagData(["page_name"]).page_name;
            AnalyticsLink({
                page_name: `${pageName}`,
                page_tool_name: `${pageName}: search filter`,
                page_tool_use: "use",
                ...AnalyticsGetUtagData(analyticsTags),
            });
            setInputFocus(true);
        }
    }

    const handleLostFocus = () => {
        setInputFocus(false);
        setSearchIcon("default");
    }

    const handleFocus = () => {
        setSearchIcon("focused");
    }

    const defaultSearchIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
            <g fill="none" fillRule="evenodd">
                <path d="M0 0H18V18H0z"/>
                <circle cx="7.125" cy="7.125" r="5.25" stroke="#666" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"/>
                <path stroke="#666" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10.837 10.837L16.125 16.125"/>
            </g>
        </svg>
    )

    const focusSearchIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
            <g fill="none" fillRule="evenodd">
                <path d="M0 0H18V18H0z"/>
                <circle cx="7.125" cy="7.125" r="5.25" stroke="#00b0ca" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"/>
                <path stroke="#00b0ca" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M10.837 10.837L16.125 16.125"/>
            </g>
        </svg>
    )

    const closeSvg = (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 30 30" onClick={handleCancel} className={styles.img}>
            <g fill="none" fillRule="evenodd">
                <path d="M0 0H30V30H0z"/>
                <path fill="#000" fillRule="nonzero" d="M7.946 6.897l.084.073L15 13.939l6.97-6.97c.293-.292.767-.292 1.06 0 .267.267.29.684.073.977l-.073.084L16.061 15l6.97 6.97c.292.293.292.767 0 1.06-.267.267-.684.29-.977.073l-.084-.073L15 16.061l-6.97 6.97c-.293.292-.767.292-1.06 0-.267-.267-.29-.684-.073-.977l.073-.084L13.939 15l-6.97-6.97c-.292-.293-.292-.767 0-1.06.267-.267.684-.29.977-.073z"/>
            </g>
        </svg>
    )

	return (
        <CustomContainer 
            className={`${styles["search-box-autosuggest-container"]}`} 
        >
            <CustomContainer className={`${styles["search-box"]} ${showOptions && searchText ? styles["no-border-radius-bottom"] : ''} ${className}`}>
                <CustomContainer className={styles.img}>
                    {searchIcon == "default" && (
                        defaultSearchIcon
                    )}
                    {searchIcon == "focused" && (
                        focusSearchIcon
                    )}
                </CustomContainer>
                <input
                    ref={inputAutoFocus}
                    type="text"
                    name={searchText}
                    className={`${styles["search-input"]} ${inputStyle}`}
                    onChange={handleOnChange}
                    value={searchText}
                    onKeyDown={handleKeypress}
                    onFocus={handleFocus}
                    id="search-box-input"
                    placeholder={isMobile || isTablet ? "" : "Type here to search"}
                    onClick={handleClick}
                    onBlur={handleLostFocus}
                />
                {closeRequired && (
                    closeSvg
                )}
            </CustomContainer>
            {showAffordabilityLabel && <div className={styles["info"]}>
                <img src={infoIconPath} className={styles["info-icon"]}/>
                <label className={styles["info-label"]}>{qualifyLabel}</label>
            </div>}
            <CustomContainer className={`${styles["search-box-autosuggest-items-container"]}`}>
                {showOptions && (
                    <ul className={`${styles["menu"]} ${styles["no-border-radius-top"]}`}>
                        {filteredOptions && filteredOptions.length > 0 && 
                            filteredOptions.slice(0, optionSize).map((option) => 
                            <li className={`${styles["menu-item"]}`} key={option.value} onClick={(e) => handleOptionClick(e, option)}>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: truncateString(option.label.replace(
                                          option.label.match(new RegExp(searchText, 'gi')),
                                          '<strong>' +
                                          option.label.match(new RegExp(searchText, 'gi')) +
                                            '</strong>'
                                        ),60),
                                      }}
                                />
                            </li>
                        )}
                        {filteredOptions.length == 0 && (
                            <li className={`${styles["menu-item"]} ${styles["no-result"]}`}>No results found</li>
                        )}
                    </ul>
                )}
            </CustomContainer>
        </CustomContainer>
	);
}
);

const mapStateToProps = (state) => ({
	hideNavItems: state.navDetails.hideNavItems,
});

export default connect(mapStateToProps,  {}, null, {
    forwardRef: true,
})(SearchBoxAutoSuggest);
